import { FormControl, FormLabel, Typography, TextField as MuiTextField, InputAdornment, TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { useCallback, useMemo, useState, ClipboardEvent, ChangeEvent } from 'react';
import { ControllerRenderProps, FieldError } from 'react-hook-form';

export interface TextFieldProps {
  name: string;
  label: string;
  disabled?: boolean;
  value: string;
  onChange?: ControllerRenderProps['onChange'];
  error?: FieldError;
  readOnly?: boolean;
  multiline?: boolean;
  rows?: number;
  textSuffix?: string;
  fullWidth?: boolean;
  helperText?: string;
  placeholder?: string;
  maxLength?: number;
  slotProps?: MuiTextFieldProps['slotProps'];
  maxWords?: number;
}

export const TextField = ({
  name,
  label,
  disabled = false,
  value,
  onChange,
  error,
  readOnly = false,
  multiline = false,
  rows = 1,
  textSuffix,
  fullWidth = true,
  helperText,
  maxLength,
  slotProps,
  placeholder,
  maxWords,
}: TextFieldProps) => {
  const inputEndAdornment = textSuffix && (
    <InputAdornment position="end">
      <Typography variant="caption">{textSuffix}</Typography>
    </InputAdornment>
  );

  const wordCounter = useCallback(
    (value?: string) => {
      if (!value) return { remainingWords: maxWords || 0, words: [] };
      const words = value.split(/\s+/).filter((word) => word.length !== 0);
      const remainingWords = maxWords && maxWords - words.length;
      return { remainingWords: remainingWords || 0, words };
    },
    [maxWords],
  );

  const [maxWordsState, setMaxWordsState] = useState(wordCounter(value));

  const charCounterText = useMemo(() => {
    if (!maxLength) return;
    const remainingCharacters = value ? maxLength - value.length : maxLength;
    return `${remainingCharacters} ${remainingCharacters === 1 ? 'character' : 'characters'} remaining`;
  }, [value, maxLength]);

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      if (maxWords && onChange) {
        event.preventDefault();
        const newValue = event.clipboardData.getData('text/plain');
        const { remainingWords, words } = wordCounter(newValue);

        if (remainingWords < 0) {
          const maxLengthWithoutSpaces = words.slice(0, remainingWords).join(' ').length;
          onChange(newValue.slice(0, maxLengthWithoutSpaces));
          setMaxWordsState({ remainingWords: 0, words: words.slice(0, remainingWords) });
        } else {
          onChange(newValue);
          setMaxWordsState({ remainingWords, words });
        }
      }
    },
    [onChange, maxWords, wordCounter],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (onChange) {
        if (maxWords) {
          const { remainingWords, words } = wordCounter(event.target.value);
          if (remainingWords >= 0) {
            if (remainingWords === 0) {
              onChange(event.target.value.trimEnd());
            } else {
              onChange(event.target.value);
            }
            setMaxWordsState({ remainingWords, words });
          }
        } else {
          onChange(event.target.value);
        }
      }
    },
    [onChange, maxWords, setMaxWordsState, wordCounter],
  );

  return (
    <>
      <FormControl fullWidth={fullWidth} size="small">
        <FormLabel id={`${name}-label`} disabled={disabled}>
          {label}
        </FormLabel>
        <MuiTextField
          size="small"
          aria-labelledby={`${name}-label`}
          value={value}
          error={!!error}
          disabled={disabled}
          onPaste={handlePaste}
          onChange={handleChange}
          slotProps={{
            ...slotProps,
            input: { ...slotProps?.input, endAdornment: inputEndAdornment },
            htmlInput: {
              ...slotProps?.htmlInput,
              readOnly,
              ...(maxLength && { maxLength: maxLength }),
            },
          }}
          multiline={multiline}
          rows={rows}
          placeholder={placeholder}
          helperText={helperText}
          data-testid={`${name}-input`}
        />
      </FormControl>
      {(maxLength || maxWords) && (
        <Typography pl={1.5} variant="caption" sx={(theme) => ({ color: disabled ? theme.palette.grey[300] : theme.palette.grey[500] })}>
          {maxLength && charCounterText}
          {maxWords && `${maxWordsState.remainingWords} ${maxWordsState.remainingWords === 1 ? 'word' : 'words'} remaining to be written`}
        </Typography>
      )}
      {error && (
        <Typography variant="caption" color="error">
          {error!.message as string}
        </Typography>
      )}
    </>
  );
};
